import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { forwardRef, useEffect, useState } from "react";
import { IMaskInput } from "react-imask";
import { useAppDispatch } from "src/hooks/redux";
import { submitAddendumOnBehalf } from "../../services/AdminDashService";
import {
  ApplicationAddendum,
  ApplicationAddendumQuestion,
  ApplicationAddendumSubmission,
} from "src/types/LeaseDomainAddendum";

interface SubmitAddendumOnBehalfModalProps {
  show: boolean;
  onHide: () => void;
  leaseId: string;
  applicantId: string;
  applicantName: string;
  // This applicant's pending addenda (the same forms for every applicant); submitted together for the application.
  addendums: ApplicationAddendum[];
  onSubmitted: () => void;
}

const isFreeText = (question: ApplicationAddendumQuestion) =>
  question.questionType === "text" || question.questionType === "single-line";
const isSingleChoice = (question: ApplicationAddendumQuestion) =>
  question.questionType === "single-select" ||
  question.questionType === "boolean";
const isMultiSelect = (question: ApplicationAddendumQuestion) =>
  question.questionType === "multi-select";

const sortQuestions = (addendum: ApplicationAddendum) =>
  [...addendum.formQuestions].sort((a, b) => a.sortOrder - b.sortOrder);

// Answers are keyed per addendum so two forms can never share state through a question id.
const answerKey = (addendumId: string, questionId: string) =>
  `${addendumId}:${questionId}`;

// AP2-6943: this data has no answer ids, so email/phone fields are recognised by their question text.
const isEmailQuestion = (question: ApplicationAddendumQuestion) =>
  /email/i.test(question.questionText);
const isPhoneQuestion = (question: ApplicationAddendumQuestion) =>
  /phone/i.test(question.questionText);

// The WHATWG grammar behind type="email", so this agrees with the browser on what is valid.
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const PHONE_LENGTH = 14;

interface PhoneMaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const PhoneNumberMask = forwardRef<HTMLInputElement, PhoneMaskProps>(
  function PhoneNumberMask(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(#00) 000-0000"
        definitions={{ "#": /[1-9]/ }}
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
        minLength={PHONE_LENGTH}
        maxLength={PHONE_LENGTH}
      />
    );
  }
);

// Mirrors the browser's required / email / minLength checks the legacy modal relied on.
const isFreeTextValid = (
  question: ApplicationAddendumQuestion,
  raw: string
) => {
  const value = raw.trim();
  if (!value) return !question.isRequired;
  if (isEmailQuestion(question)) return EMAIL_PATTERN.test(value);
  if (isPhoneQuestion(question)) return raw.length === PHONE_LENGTH;
  return true;
};

const invalidOutlineSx = {
  "& label.Mui-focused": { color: "black" },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: "black", borderWidth: "1px" },
    "input:invalid + fieldset": { borderColor: "red" },
  },
};

const SubmitAddendumOnBehalfModal = ({
  show,
  onHide,
  leaseId,
  applicantId,
  applicantName,
  addendums,
  onSubmitted,
}: SubmitAddendumOnBehalfModalProps) => {
  const dispatch = useAppDispatch();
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<
    Record<string, string[]>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);

  // Reset only on the show:false->true transition, not on every `addendums` identity change -
  // Applicants.tsx passes a freshly-derived array on every render, so keying on it too would
  // wipe in-progress answers on any unrelated parent re-render while the modal is open.
  useEffect(() => {
    if (!show) return;
    setTextValues({});
    setSelectedAnswerIds({});
    setShowEmptyConfirm(false);
  }, [show]);

  const unsupportedQuestion = addendums
    .flatMap((addendum) => addendum.formQuestions)
    .find(
      (question) =>
        !isFreeText(question) &&
        !isSingleChoice(question) &&
        !isMultiSelect(question)
    );

  const toggleAnswer = (key: string, answerId: string, checked: boolean) => {
    setSelectedAnswerIds((prev) => {
      const current = prev[key] ?? [];
      return {
        ...prev,
        [key]: checked
          ? [...current, answerId]
          : current.filter((id) => id !== answerId),
      };
    });
  };

  // Multi-select is required only through minimumAnswers, so an optional checkbox form (Stove
  // Knob) can be submitted with nothing ticked - matching Lease Domain's own rule.
  const isQuestionValid = (
    addendum: ApplicationAddendum,
    question: ApplicationAddendumQuestion
  ) => {
    const key = answerKey(addendum.addendumId, question.formQuestionId);
    if (isFreeText(question)) {
      return isFreeTextValid(question, textValues[key] ?? "");
    }
    const selected = selectedAnswerIds[key] ?? [];
    if (isMultiSelect(question)) {
      return selected.length >= (question.minimumAnswers ?? 0);
    }
    return !question.isRequired || selected.length > 0;
  };

  const isFormValid = addendums.every((addendum) =>
    addendum.formQuestions.every((question) =>
      isQuestionValid(addendum, question)
    )
  );

  // Options post their own answerText, not their id - Lease Domain matches on answerText, the
  // same shape the resident-facing UI submits. Forms with nothing answered are omitted.
  const buildAddenda = (): ApplicationAddendumSubmission[] =>
    addendums.flatMap((addendum) => {
      const answers = sortQuestions(addendum).flatMap((question) => {
        const key = answerKey(addendum.addendumId, question.formQuestionId);
        if (isFreeText(question)) {
          const typed = (textValues[key] ?? "").trim();
          const value = isPhoneQuestion(question)
            ? typed.replace(/\D/g, "")
            : typed;
          return value
            ? [{ questionId: question.formQuestionId, answerValue: value }]
            : [];
        }
        return (selectedAnswerIds[key] ?? []).flatMap((answerId) => {
          const option = question.formQuestionAnswers.find(
            (answer) => answer.answerId === answerId
          );
          return option
            ? [
                {
                  questionId: question.formQuestionId,
                  answerValue: option.answerText,
                },
              ]
            : [];
        });
      });
      return answers.length
        ? [{ addendumId: addendum.addendumId, answers }]
        : [];
    });

  const submit = async (addenda: ApplicationAddendumSubmission[]) => {
    setIsSubmitting(true);
    const result = await dispatch(
      submitAddendumOnBehalf(leaseId, applicantId, addenda)
    );
    setIsSubmitting(false);

    // Someone else submitting first still leaves the addenda complete, so refresh either way.
    if (result !== "failed") {
      onSubmitted();
      onHide();
    }
  };

  const handleSubmit = async () => {
    if (addendums.length === 0 || unsupportedQuestion) return;
    if (!isFormValid) return;

    const addenda = buildAddenda();
    // An empty submission still locks every addendum for every applicant, so it has to be deliberate.
    if (addenda.length === 0) {
      setShowEmptyConfirm(true);
      return;
    }
    await submit(addenda);
  };

  const handleConfirmEmptySubmit = async () => {
    setShowEmptyConfirm(false);
    await submit([]);
  };

  const renderQuestion = (
    addendum: ApplicationAddendum,
    question: ApplicationAddendumQuestion
  ) => {
    const key = answerKey(addendum.addendumId, question.formQuestionId);
    const sortedAnswers = [...question.formQuestionAnswers].sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    if (isFreeText(question)) {
      const isEmail = isEmailQuestion(question);
      const isPhone = !isEmail && isPhoneQuestion(question);
      return (
        <TextField
          label={question.questionText}
          name={key}
          variant="outlined"
          fullWidth
          focused
          required={question.isRequired}
          // Typed-but-invalid shows red too, including a whitespace-only required value the browser accepts.
          error={
            (textValues[key] ?? "").length > 0 &&
            !isFreeTextValid(question, textValues[key] ?? "")
          }
          type={isEmail ? "email" : "text"}
          placeholder={isPhone ? "(#00) 000-0000" : undefined}
          value={textValues[key] ?? ""}
          onChange={(event) =>
            setTextValues((prev) => ({ ...prev, [key]: event.target.value }))
          }
          sx={invalidOutlineSx}
          InputProps={{
            inputComponent: isPhone ? (PhoneNumberMask as any) : undefined,
          }}
        />
      );
    }
    if (isSingleChoice(question)) {
      return (
        <FormControl required={question.isRequired}>
          <FormLabel id={`label-${key}`}>{question.questionText}</FormLabel>
          <RadioGroup
            aria-labelledby={`label-${key}`}
            name={`question-${key}`}
            value={(selectedAnswerIds[key] ?? [])[0] ?? ""}
            onChange={(event) =>
              setSelectedAnswerIds((prev) => ({
                ...prev,
                [key]: [event.target.value],
              }))
            }
          >
            {sortedAnswers.map((answer) => (
              <FormControlLabel
                key={answer.answerId}
                value={answer.answerId}
                control={<Radio />}
                label={answer.answerText}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }
    if (isMultiSelect(question)) {
      return (
        <FormControl>
          <FormLabel id={`label-${key}`}>{question.questionText}</FormLabel>
          <FormGroup aria-labelledby={`label-${key}`}>
            {sortedAnswers.map((answer) => (
              <FormControlLabel
                key={answer.answerId}
                control={
                  <Checkbox
                    checked={(selectedAnswerIds[key] ?? []).includes(
                      answer.answerId
                    )}
                    onChange={(event) =>
                      toggleAnswer(key, answer.answerId, event.target.checked)
                    }
                  />
                }
                label={answer.answerText}
              />
            ))}
          </FormGroup>
        </FormControl>
      );
    }
    return (
      <Alert severity="warning">
        Unsupported question type &quot;{question.questionType}&quot;.
      </Alert>
    );
  };

  return (
    <>
      <Dialog
        open={show}
        onClose={onHide}
        fullWidth
        maxWidth="md"
        aria-labelledby="submit-addendum-on-behalf-modal"
      >
        <Paper>
          <DialogTitle id="submit-addendum-on-behalf-modal">
            Review lease addendums
          </DialogTitle>
          <Divider />
          <DialogContent>
            <DialogContentText mb={1} fontSize={17}>
              Answering on behalf of {applicantName}.
            </DialogContentText>
            <DialogContentText mb={3} fontSize={17}>
              The applicant needs to provide some additional information before
              you can generate their lease. The applicant can fill out this
              information by logging into the applicant dashboard and clicking
              on&nbsp;<b>“Review Lease Addendums,”</b> or you can contact the
              applicant and make these selections on their behalf. Only one
              applicant needs to make these selections. Once submitted, this
              information <b>cannot</b> be altered.
            </DialogContentText>

            {unsupportedQuestion && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                An addendum has a question type this form doesn&apos;t support
                yet (&quot;{unsupportedQuestion.questionType}&quot;) - the
                addenda can&apos;t be submitted here.
              </Alert>
            )}

            {addendums.map((addendum, index) => (
              <Accordion
                key={addendum.addendumId}
                defaultExpanded={index === 0}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-${addendum.addendumId}`}
                  id={`header-${addendum.addendumId}`}
                >
                  <Typography>
                    {addendum.formTitle ?? addendum.addendumId}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails id={`panel-${addendum.addendumId}`}>
                  {/* AP2-6943: legal text as HTML, from Lease Domain's own form seed, as legacy renders it. */}
                  {addendum.formDescription && (
                    <Typography paragraph sx={{ whiteSpace: "pre-line" }} p={0}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: addendum.formDescription,
                        }}
                      />
                    </Typography>
                  )}
                  {sortQuestions(addendum).map((question) => (
                    <div
                      key={question.formQuestionId}
                      style={{ marginTop: 16 }}
                    >
                      {renderQuestion(addendum, question)}
                    </div>
                  ))}
                  <FormHelperText>{addendum.formDisclaimer}</FormHelperText>
                </AccordionDetails>
              </Accordion>
            ))}
          </DialogContent>
          <DialogActions style={{ justifyContent: "space-between" }}>
            <Button
              onClick={onHide}
              disabled={isSubmitting}
              variant="text"
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                addendums.length === 0 ||
                !!unsupportedQuestion ||
                !isFormValid
              }
              variant="text"
              color="success"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
      <Dialog
        open={showEmptyConfirm}
        onClose={() => setShowEmptyConfirm(false)}
        fullWidth
        maxWidth="xs"
      >
        <Paper>
          <DialogTitle>
            <Typography>Submit Without Answers?</Typography>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>
              No answers were selected. Submitting will mark every addendum
              complete for all applicants on this application, and it can&apos;t
              be changed. Submit anyway?
            </Typography>
          </DialogContent>
          <DialogActions style={{ justifyContent: "space-between" }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => setShowEmptyConfirm(false)}
              data-testid="confirm-empty-addenda-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="text"
              color="success"
              onClick={handleConfirmEmptySubmit}
              data-testid="confirm-empty-addenda-submit-button"
            >
              Submit Anyway
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
    </>
  );
};

export default SubmitAddendumOnBehalfModal;
